/** Limits are logical per-source budgets; the browser may retain other decoded frames. */
export const MAX_ALPHA_VIDEO_DIMENSION = 8192;
export const MAX_ALPHA_VIDEO_PIXELS = 16_777_216;
export const MAX_ACTIVE_ALPHA_VIDEO_PIXELS = 33_554_432;

export function assertAlphaVideoIntervalBudget(intervals:ReadonlyArray<{from:number;end:number;pixels:number}>) {
  const events=intervals.flatMap(({from,end,pixels})=>[
    {frame:from,delta:pixels},{frame:end,delta:-pixels},
  ]).sort((a,b)=>a.frame-b.frame||a.delta-b.delta);
  let active=0;
  for(const event of events){
    active+=event.delta;
    if(active>MAX_ACTIVE_ALPHA_VIDEO_PIXELS)throw Error(
      '동시에 표시되는 투명 영상의 해상도 합계가 브라우저 출력 한도를 넘었습니다. 일부 영상을 줄여 다시 시도해 주세요.',
    );
  }
}

export function assertAlphaVideoDimensions(width:number,height:number) {
  if (!Number.isSafeInteger(width) || !Number.isSafeInteger(height) || width < 1 || height < 1 ||
      width > MAX_ALPHA_VIDEO_DIMENSION || height > MAX_ALPHA_VIDEO_DIMENSION ||
      width * height > MAX_ALPHA_VIDEO_PIXELS)
    throw Error('투명 영상 해상도가 브라우저 출력 한도를 넘었습니다. 원본 크기를 줄여 다시 시도해 주세요.');
  return width * height;
}

/** The parent Loop, when present, supplies a local frame just like preview OffthreadVideo. */
export function alphaVideoSourceTime(options:{frame:number;fps:number;trimBefore:number;playbackRate:number;duration:number}) {
  const {frame,fps,trimBefore,playbackRate,duration}=options;
  if (!Number.isFinite(frame) || !Number.isFinite(fps) || fps<=0 || !Number.isFinite(trimBefore) ||
      !Number.isFinite(playbackRate) || playbackRate<=0 || !Number.isFinite(duration) || duration<=0)
    throw Error('투명 영상의 재생 시간 정보를 확인하지 못했습니다.');
  const rawStart=trimBefore/fps;
  const start=Math.max(0,rawStart);
  const remaining=duration-start;
  if (remaining<=0)throw Error('투명 영상의 시작 시간이 원본 길이를 벗어났습니다.');
  const elapsed=Math.max(0,frame)*playbackRate/fps+Math.min(0,rawStart);
  const position=Math.min(elapsed,remaining);
  // A seek to exactly duration can resolve with no frame. Keep the final decoded frame.
  return Math.min(duration-0.000001,Math.max(0,start+position));
}

function waitForVideo(video:HTMLVideoElement,event:'loadeddata'|'seeked',signal:AbortSignal):Promise<void>{
  return new Promise((resolve,reject)=>{
    if(signal.aborted){reject(signal.reason);return;}
    if(video.error){reject(Error('투명 영상 원본을 읽거나 디코딩하지 못했습니다. 원본 파일을 확인해 주세요.'));return;}
    const done=(error?:Error)=>{video.removeEventListener(event,onReady);video.removeEventListener('error',onError);signal.removeEventListener('abort',onAbort);error?reject(error):resolve();};
    const onReady=()=>done();
    const onError=()=>done(Error('투명 영상 원본을 읽거나 디코딩하지 못했습니다. 원본 파일을 확인해 주세요.'));
    const onAbort=()=>done(signal.reason instanceof Error?signal.reason:Error('투명 영상 처리가 중단됐습니다.'));
    video.addEventListener(event,onReady,{once:true});
    video.addEventListener('error',onError,{once:true});
    signal.addEventListener('abort',onAbort,{once:true});
  });
}

export async function drawAlphaVideoFrame(video:HTMLVideoElement,canvas:HTMLCanvasElement,
  clock:{frame:number;fps:number;trimBefore:number;playbackRate:number},signal:AbortSignal,declaredPixels:number){
  if(signal.aborted)throw signal.reason;
  // HAVE_CURRENT_DATA = 2; use the value directly so this helper is testable without a DOM global.
  if(video.readyState<2)await waitForVideo(video,'loadeddata',signal);
  if(signal.aborted)throw signal.reason;
  const width=video.videoWidth,height=video.videoHeight;
  const decodedPixels=assertAlphaVideoDimensions(width,height);
  if(!Number.isSafeInteger(declaredPixels)||declaredPixels<1||decodedPixels>declaredPixels)
    throw Error('투명 영상의 실제 해상도가 저장된 원본 정보보다 큽니다. 원본을 다시 등록해 주세요.');
  const time=alphaVideoSourceTime({...clock,duration:video.duration});
  if(Math.abs(video.currentTime-time)>0.00001||video.readyState<2){
    const seeking=waitForVideo(video,'seeked',signal);
    video.currentTime=time;
    await seeking;
  }
  if(signal.aborted)throw signal.reason;
  const context=canvas.getContext('2d',{alpha:true});
  if(!context)throw Error('투명 영상 출력 화면을 만들지 못했습니다.');
  if(canvas.width!==width||canvas.height!==height){canvas.width=width;canvas.height=height;}
  context.clearRect(0,0,width,height);
  context.drawImage(video,0,0,width,height);
  try{context.getImageData(0,0,1,1);}catch(error){throw Error('투명 영상 원본을 읽을 권한이 없어 출력할 수 없습니다.',{cause:error});}
}
